import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { awaitJSON, StreamingV2LambdaFetch, fetchPresigned } from '../utils/fetch-utils';
import { AppDispatch, RootState } from '.';
import { useSetError } from './modals';
import { makeReducer } from './utils';

const ROOT = 'networks';

const SET_NETWORK_MAP = `${ROOT}/SET_NETWORK_MAP`;
const SET_V1_NETWORK_MAP = `${ROOT}/SET_V1_NETWORK_MAP`;
const SET_DERIVED_NETWORK_MAP = `${ROOT}/SET_DERIVED_NETWORK_MAP`;

export interface Network {
  shortCode: string;
  networkGroup: string;
  name: string;
  color: string;
  pdfText: string;
  defaultIOPdfText: string;
  requiresDescriptions: boolean;
  parent?: string;
  buyable: boolean;
  children?: string[];
  platforms: string[];
}
type NetworkMap = Record<string, Network>;
interface V1Network {
  cpm: string;
  name: string;
  networkGroup: string;
  shortCode: string;
  color: string;
  networkGroupColor: string;
  platforms: string[];
  hidden: boolean;
  specialType: string;
  durationTags: boolean;
  dsp: string | null;
}
type V1NetworkMap = Record<string, V1Network>;

export interface DerivedNetwork {
  network: string;
  description: string;
  platform: string;
  company: string;
  networkGroup: string;
  dsp?: string;
}

export type DerivedNetworkMap = Record<string, DerivedNetwork | undefined>;

interface NetworkReduxState {
  networkMap?: NetworkMap;
  v1NetworkMap?: V1NetworkMap;
  derivedNetworkMap?: DerivedNetworkMap;
}

export const networkMapSelector = (state: RootState): NetworkMap | undefined =>
  state[ROOT].networkMap;
export const v1NetworkMapSelector = (state: RootState): V1NetworkMap | undefined =>
  state[ROOT].v1NetworkMap;

export const derivedNetworkMapSelector = (state: RootState): DerivedNetworkMap | undefined =>
  state[ROOT].derivedNetworkMap;

export const useNetworkMap = (): NetworkMap | undefined => {
  const dispatch = useDispatch<AppDispatch>();
  const setError = useSetError();
  const networkMap = useSelector(networkMapSelector);
  useEffect(() => {
    if (!networkMap) {
      let bail = false;
      (async () => {
        try {
          const res = await StreamingV2LambdaFetch('/networks');
          const networks = await awaitJSON(res);
          if (bail) {
            return;
          }
          dispatch({
            type: SET_NETWORK_MAP,
            networkMap: networks
          });
        } catch (e) {
          setError({
            message: `Couldn't fetch network map. Error: ${e.message}`,
            reportError: e
          });
        }
      })();
      return () => {
        bail = true;
      };
    }
    return undefined;
  }, [networkMap, dispatch, setError]);
  return networkMap;
};

export const useDerivedNetworkMap = (
  company: string,
  testParams?: Record<string, any>
): DerivedNetworkMap | undefined => {
  const dispatch = useDispatch<AppDispatch>();
  const setError = useSetError();
  const derivedNetworkMap = useSelector(derivedNetworkMapSelector);
  useEffect(() => {
    if (!derivedNetworkMap) {
      let bail = false;
      (async () => {
        try {
          let params = { company };
          if (testParams) {
            params = { ...params, ...testParams };
          }

          const res = await StreamingV2LambdaFetch('/derived', { params });
          const parsedRes = await awaitJSON<Record<string, DerivedNetwork> | { url: string }>(res);

          /*
           * Depending on the response size, the response may be a URL to an S3 object.
           * If it is, we need to fetch the S3 object and parse it.
           */
          let derivedNetworks: Record<string, DerivedNetwork>;
          if (parsedRes.url) {
            const s3Res = await fetchPresigned(
              parsedRes.url as string,
              'Derived networks download failed'
            );
            const parsedS3Res = await awaitJSON(s3Res);
            derivedNetworks = parsedS3Res;
          } else {
            derivedNetworks = parsedRes as Record<string, DerivedNetwork>;
          }

          if (bail) {
            return;
          }
          dispatch({
            type: SET_DERIVED_NETWORK_MAP,
            derivedNetworkMap: derivedNetworks
          });
        } catch (e) {
          setError({
            message: `Couldn't fetch derived network map. Error: ${e.message}`,
            reportError: e
          });
        }
      })();
      return () => {
        bail = true;
      };
    }
    return undefined;
  }, [derivedNetworkMap, dispatch, setError, company, testParams]);
  return derivedNetworkMap;
};

const setNetworkMap = (
  state: NetworkReduxState,
  { networkMap }: { networkMap: NetworkMap }
): NetworkReduxState => ({
  ...state,
  networkMap
});

const setV1NetworkMap = (
  state: NetworkReduxState,
  { networkMap }: { networkMap: V1NetworkMap }
): NetworkReduxState => ({
  ...state,
  v1NetworkMap: networkMap
});

const setDerivedNetworkMap = (
  state: NetworkReduxState,
  { derivedNetworkMap }: { derivedNetworkMap: DerivedNetworkMap }
): NetworkReduxState => ({
  ...state,
  derivedNetworkMap
});

export default makeReducer<NetworkReduxState>(
  {},
  {
    [SET_NETWORK_MAP]: setNetworkMap,
    [SET_V1_NETWORK_MAP]: setV1NetworkMap,
    [SET_DERIVED_NETWORK_MAP]: setDerivedNetworkMap
  }
);
